import { createApiClient, type TokenStore } from '@finmin/api-client';
import { AUTH_FAILURE_EVENT, createTokenStore } from '@finmin/auth';

/**
 * sessionStorage key for the customer dashboard unmask window
 * (features/unmask). Owned here so it is wiped alongside the auth tokens on
 * EVERY clear() — both explicit logout (AuthProvider onClear) and the
 * refresh-failure logout (api-client interceptor) call tokenStore.clear(), so
 * the unmasked state can never survive a logout, even a same-user re-login in
 * the same tab (sessionStorage outlives that).
 */
export const UNMASK_STORAGE_KEY = 'finmin.user.unmask';

/**
 * localStorage key for the PAN the customer is currently working as
 * (features/dashboard/pan-view-context). localStorage rather than
 * sessionStorage because the choice should hold across tabs, and it is
 * wiped on clear() for the same reason as the unmask window: a re-login in
 * the same browser must not inherit the previous customer's PAN.
 */
export const PAN_VIEW_STORAGE_KEY = 'finmin.user.panView';

const baseTokenStore = createTokenStore();

export const tokenStore: TokenStore = {
  ...baseTokenStore,
  clear() {
    baseTokenStore.clear();
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(UNMASK_STORAGE_KEY);
      window.localStorage.removeItem(PAN_VIEW_STORAGE_KEY);
    }
  },
};

export const apiClient = createApiClient({
  baseURL: import.meta.env.VITE_API_URL ?? '',
  tokenStore,
  onAuthFailure: () => {
    // DO NOT call window.location.assign here — that causes a hard page
    // reload, which on iOS Safari + slow networks flickers as "reload
    // again and again" (regressed Apr 2026).
    //
    // Instead, fire a window event. AuthProvider listens for it and flips
    // status -> 'unauthenticated'; the route guard then redirects to
    // /login as a soft SPA navigation. Same outcome, no full reload.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(AUTH_FAILURE_EVENT));
    }
  },
});
